<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@chatwoot/pico-search';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Label from 'dashboard/components-next/label/Label.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});
const searchQuery = ref('');
const visibilityFilter = ref('all');

const records = computed(() => getters['labels/getLabels'].value);

const sidebarCount = computed(
  () => records.value.filter(label => label.show_on_sidebar).length
);

const visibilityOptions = computed(() => [
  {
    value: 'all',
    label: t('LABEL_MGMT.REDESIGN.FILTER_ALL'),
    count: records.value.length,
  },
  {
    value: 'sidebar',
    label: t('LABEL_MGMT.REDESIGN.FILTER_SIDEBAR'),
    count: sidebarCount.value,
  },
  {
    value: 'hidden',
    label: t('LABEL_MGMT.REDESIGN.FILTER_HIDDEN'),
    count: records.value.length - sidebarCount.value,
  },
]);

const filteredRecords = computed(() => {
  const byVisibility = records.value.filter(label => {
    if (visibilityFilter.value === 'sidebar') return label.show_on_sidebar;
    if (visibilityFilter.value === 'hidden') return !label.show_on_sidebar;
    return true;
  });
  const query = searchQuery.value.trim();
  const found = query
    ? picoSearch(byVisibility, query, [
        { name: 'title', weight: 4 },
        'description',
      ])
    : [...byVisibility];
  return query
    ? found
    : found.sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'));
});
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LABEL_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('LABEL_MGMT.LIST.404')"
  >
    <template #header>
      <div class="rounded-2xl border border-n-weak bg-n-solid-2 p-5 sm:p-6">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div class="flex min-w-0 items-start gap-3">
            <span
              class="grid size-11 shrink-0 place-items-center rounded-xl bg-n-alpha-2"
            >
              <Icon icon="i-hugeicons-tag-01" class="size-6 text-n-slate-11" />
            </span>
            <div class="min-w-0">
              <h1 class="mb-1 text-xl font-semibold text-n-slate-12">
                {{ $t('LABEL_MGMT.HEADER') }}
              </h1>
              <p class="mb-0 max-w-xl text-sm leading-relaxed text-n-slate-11">
                {{ $t('LABEL_MGMT.REDESIGN.INTRO') }}
              </p>
            </div>
          </div>
          <Button
            icon="i-hugeicons-add-01"
            :label="$t('LABEL_MGMT.HEADER_BTN_TXT')"
            data-testid="add-label"
            @click="openAddPopup"
          />
        </div>
        <div
          class="mt-5 flex flex-wrap items-center gap-3 border-t border-n-weak pt-4"
        >
          <Input
            v-model="searchQuery"
            class="w-full sm:max-w-sm"
            :placeholder="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
            :aria-label="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
          />
          <div
            class="flex flex-wrap items-center gap-1 rounded-lg bg-n-alpha-2 p-1"
            role="radiogroup"
            :aria-label="$t('LABEL_MGMT.REDESIGN.FILTER_LABEL')"
          >
            <button
              v-for="option in visibilityOptions"
              :key="option.value"
              type="button"
              role="radio"
              :aria-checked="visibilityFilter === option.value"
              class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
              :class="
                visibilityFilter === option.value
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              :data-testid="`label-filter-${option.value}`"
              @click="visibilityFilter = option.value"
            >
              {{ option.label }}
              <span
                class="rounded bg-n-alpha-2 px-1 tabular-nums"
                :class="visibilityFilter === option.value ? 'text-n-brand' : ''"
              >
                {{ option.count }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <span
        v-if="!filteredRecords.length"
        class="flex flex-1 items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{
          searchQuery
            ? $t('LABEL_MGMT.NO_RESULTS')
            : $t('LABEL_MGMT.REDESIGN.EMPTY_FILTER')
        }}
      </span>

      <div
        v-else
        class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
        data-testid="label-grid"
      >
        <div
          v-for="label in filteredRecords"
          :key="label.id"
          class="group relative flex min-w-0 flex-col justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4 transition-colors hover:border-n-strong"
          :data-testid="`label-card-${label.id}`"
        >
          <button
            type="button"
            class="absolute inset-0 z-10 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :aria-label="`${$t('LABEL_MGMT.FORM.EDIT')}: ${label.title}`"
            @click="openEditPopup(label)"
          />
          <div class="grid min-w-0 gap-2">
            <Label :label="label" color-border class="max-w-full" />
            <p
              class="mb-0 line-clamp-2 break-words text-xs leading-relaxed"
              :class="label.description ? 'text-n-slate-11' : 'text-n-slate-10'"
            >
              {{
                label.description || $t('LABEL_MGMT.REDESIGN.NO_DESCRIPTION')
              }}
            </p>
          </div>
          <div
            class="flex items-center justify-between gap-2 border-t border-n-weak pt-3"
          >
            <span
              class="flex min-w-0 items-center gap-2 text-xs text-n-slate-11"
            >
              <span
                class="size-3 shrink-0 rounded-full border border-solid border-n-weak"
                :style="{ backgroundColor: label.color }"
              />
              <span class="font-mono uppercase">{{ label.color }}</span>
              <span
                v-tooltip.top="
                  label.show_on_sidebar
                    ? $t('LABEL_MGMT.REDESIGN.ON_SIDEBAR')
                    : $t('LABEL_MGMT.REDESIGN.HIDDEN_FROM_SIDEBAR')
                "
                class="size-3.5 shrink-0"
                :class="
                  label.show_on_sidebar
                    ? 'i-hugeicons-sidebar-left text-n-slate-11'
                    : 'i-hugeicons-view-off-slash text-n-slate-10'
                "
                role="img"
                :aria-label="
                  label.show_on_sidebar
                    ? $t('LABEL_MGMT.REDESIGN.ON_SIDEBAR')
                    : $t('LABEL_MGMT.REDESIGN.HIDDEN_FROM_SIDEBAR')
                "
              />
            </span>
            <div class="flex shrink-0 items-center gap-1">
              <span
                class="i-hugeicons-arrow-right-02 size-4 text-n-slate-11"
                aria-hidden="true"
              />
              <Button
                v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                icon="i-hugeicons-delete-02"
                color="slate"
                variant="ghost"
                size="sm"
                class="relative z-20 hover:enabled:bg-n-ruby-2 hover:enabled:text-n-ruby-11"
                :aria-label="`${$t('LABEL_MGMT.FORM.DELETE')}: ${label.title}`"
                :is-loading="loading[label.id]"
                :data-testid="`delete-label-${label.id}`"
                @click.stop="openDeletePopup(label)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
